/**
 * Subdomain Detection Utility
 * Detects if current URL is a company subdomain and extracts company information
 */

/**
 * Get subdomain information from current hostname
 * @returns {Object} Subdomain info with isSubdomain, subdomain, companyName
 */
export const getSubdomainInfo = () => {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  
  // Development: localhost or IP
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.')) {
    // Check for test subdomain in query params for local testing
    const urlParams = new URLSearchParams(window.location.search);
    const testSubdomain = urlParams.get('subdomain');
    
    if (testSubdomain) {
      return {
        isSubdomain: true,
        subdomain: testSubdomain,
        companyName: testSubdomain,
        isLocalTest: true
      };
    }
    
    return {
      isSubdomain: false,
      subdomain: null,
      companyName: null,
      isLocalTest: false
    };
  }

  // Treat Amplify App domains as main domain (not a tenant subdomain)
  if (hostname.endsWith('amplifyapp.com')) {
    // Check for test subdomain in query params for feature branch testing
    const urlParams = new URLSearchParams(window.location.search);
    const testSubdomain = urlParams.get('subdomain');
    
    if (testSubdomain) {
      return {
        isSubdomain: true,
        subdomain: testSubdomain,
        companyName: testSubdomain,
        isLocalTest: true
      };
    }

     return {
      isSubdomain: false,
      subdomain: null,
      companyName: null,
      isLocalTest: false
    };
  }
  
  // Production: Check if subdomain exists
  if (parts.length >= 3) {
    const subdomain = parts[0];
    
    // Ignore common non-company subdomains
    const ignoredSubdomains = ['www', 'tempclass', 'admin', 'api', 'test', 'staging', 'development', 'app'];
    if (ignoredSubdomains.includes(subdomain.toLowerCase())) {
      return {
        isSubdomain: false,
        subdomain: null,
        companyName: null,
        isLocalTest: false
      };
    }
    
    return {
      isSubdomain: true,
      subdomain,
      companyName: subdomain,
      isLocalTest: false
    };
  }
  
  // Base domain (no subdomain)
  return {
    isSubdomain: false,
    subdomain: null,
    companyName: null,
    isLocalTest: false
  };
};

/**
 * Sanitize company name to create valid subdomain
 * Matches the sanitization logic in create-subdomain-lambda.py
 * @param {string} companyName - Company name to sanitize
 * @returns {string} DNS-safe subdomain
 */
const sanitizeSubdomain = (companyName) => {
  if (!companyName) return '';
  
  let subdomain = companyName.toLowerCase();
  // Replace non-alphanumeric characters with hyphens
  subdomain = subdomain.replace(/[^a-z0-9-]/g, '-');
  // Replace multiple consecutive hyphens with single hyphen
  subdomain = subdomain.replace(/-+/g, '-');
  // Remove leading/trailing hyphens
  subdomain = subdomain.replace(/^-+|-+$/g, '');
  
  return subdomain;
};

/**
 * Resolve the active base domain (e.g. "tempcomputing.com") from the current
 * hostname so subdomain redirects stay on whichever brand domain the user is
 * already on. Falls back to "tempcomputing.com" (the future primary domain).
 */
export const getActiveBaseDomain = () => {
  const hostname = window.location.hostname;
  if (hostname.includes('tempcomputing.com')) return 'tempcomputing.com';
  if (hostname.includes('tempclass.com')) return 'tempclass.com';
  return 'tempcomputing.com';
};

/**
 * Build company subdomain URL
 * @param {string} companyName - Company name
 * @param {string} baseDomain - Base domain (default: derived from current hostname)
 * @param {string} path - Optional path to append
 * @returns {string} Full subdomain URL
 */
const buildSubdomainUrl = (companyName, baseDomain = getActiveBaseDomain(), path = '') => {
  const subdomain = sanitizeSubdomain(companyName);
  const protocol = window.location.protocol; // http: or https:
  
  // For localhost, append subdomain as query param for testing
  if (window.location.hostname === 'localhost') {
    return `${protocol}//${window.location.host}${path}?subdomain=${subdomain}`;
  }
  
  return `${protocol}//${subdomain}.${baseDomain}${path}`;
};

/**
 * Detect whether OAuth callback parameters are present in URL search/hash
 * @returns {boolean}
 */
export const isOAuthCallbackInProgress = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const hash = window.location.hash?.startsWith('#')
    ? window.location.hash.substring(1)
    : window.location.hash;
  const hashParams = new URLSearchParams(hash || '');

  return (
    searchParams.has('code') ||
    searchParams.has('state') ||
    searchParams.has('error') ||
    hashParams.has('access_token') ||
    hashParams.has('id_token') ||
    hashParams.has('error')
  );
};

/**
 * Check if user should be redirected to company subdomain
 * @param {Object} user - Cognito user object
 * @param {Object} subdomainInfo - Current subdomain info
 * @returns {string|null} Redirect URL or null if no redirect needed
 */
export const getSubdomainRedirect = (user, subdomainInfo) => {
  if (!user) return null;

  // NOTE: We intentionally do NOT gate on isOAuthCallbackInProgress() here.
  // By the time this function is called from checkAccountStatus, isFullyAuthenticated
  // is true — meaning Amplify has already completed the OAuth code exchange and the
  // session is established. Suppressing the redirect here caused content to render on
  // the wrong domain on first login (the redirect was only triggered on the second
  // visit, when OAuth params were gone from the URL).
  //
  // For amplifyapp.com we strip any residual OAuth params from the redirect URL
  // below so Cognito does not attempt to re-process a stale code.

  // Get company name from user attributes or custom claims
  const userCompanyName = user.attributes?.['custom:company_name'] || 
                          user.signInUserSession?.idToken?.payload?.['custom:company_name'];
  
  if (!userCompanyName) return null;
  
  const userSubdomain = sanitizeSubdomain(userCompanyName);

  // Handle Amplify Feature Branches using query parameters
  if (window.location.hostname.endsWith('amplifyapp.com')) {
    // If query param subdomain matches user subdomain, we are good.
    if (subdomainInfo.isSubdomain && subdomainInfo.subdomain === userSubdomain) {
      return null;
    }
    
    // Build redirect URL and strip any OAuth callback params so they are not
    // replayed on the reloaded page (which would cause a Cognito 400 error).
    const url = new URL(window.location.href);
    url.searchParams.delete('code');
    url.searchParams.delete('state');
    url.searchParams.delete('error');
    url.searchParams.set('subdomain', userSubdomain);
    return url.toString();
  }
  
  // If user is on main domain, redirect to their company subdomain.
  if (!subdomainInfo.isSubdomain) {
    const targetUrl = buildSubdomainUrl(userCompanyName, getActiveBaseDomain(), '/');
    // Ensure all OAuth params are stripped from the redirect URL since we are
    // already authenticated; replaying them would cause Cognito 400 errors.
    const url = new URL(targetUrl);
    url.searchParams.delete('code');
    url.searchParams.delete('state');
    url.searchParams.delete('error');
    return url.toString();
  }
  
  // If user is on wrong company subdomain, redirect to their company
  if (subdomainInfo.isSubdomain && subdomainInfo.subdomain !== userSubdomain) {
    const targetUrl = buildSubdomainUrl(userCompanyName, getActiveBaseDomain(), '/');
    const url = new URL(targetUrl);
    url.searchParams.delete('code');
    url.searchParams.delete('state');
    url.searchParams.delete('error');
    return url.toString();
  }
  
  return null; // No redirect needed
};
